/**
 * 平台: 小程序webview
 * 通用能力:
 * 1.bridge：mpReady (window.my | window.wx)
 * 2.登录: mpLogin
 * 3.定位: mpLocate
 * 4.订阅: mpSubscribe
 * 5.分享: mpShare
 * 6.日历提醒: wxmpCalendar
 */
import { utils, payFormatParams } from '@didi/webx-js-core';
import { build } from '../../../query';
import { is, xenv } from '../../../xenv';
import { weixinReady, weixinLocate } from './weixin';
import { sdkReady } from '../../loader';
import { MiniprogramJump, AliprogramJump, DyprogramJump, HwfastappJump } from './goTo';
import { query, hashQuery } from './../../../query';
// import mpBridge from '@didi/webx-mp-bridge'
import { utils as webUtils } from '../../../utils';
import { request } from '../../../request';
import { getPayParams, miniProgramFormat } from '../../../pay/payParams';
import { storage } from '../../../storage';
import { supportNewTransferStationPageXenv } from './transfer-station-conf';
/**
 * 微信小程序commonJump dchnJump
 */
function wxCommonJump(loadParams, xenv, commonParams = {}) {
    new MiniprogramJump(loadParams, xenv, commonParams).doJump();
}
/**
 * 支付宝小程序commonJump dchnJump
 */
function aliCommonJump(loadParams, xenv, commonParams = {}) {
    new AliprogramJump(loadParams, xenv, commonParams).doJump();
}
/**
 * 抖音小程序commonJump dchnJump
 */
function dyCommonJump(loadParams, xenv, commonParams = {}) {
    new DyprogramJump(loadParams, xenv, commonParams).doJump();
}
/**
 * 华为快应用webapp webview dchnJump
 */
function webappCommonJump(loadParams, xenv, commonParams = {}) {
    new HwfastappJump(loadParams, xenv, commonParams).doJump();
}
/**
 * 支付宝小程序webview bridgeReady
 */
const alimpReady = (callback) => {
    const _alipayMpSDK = 'https://appx/web-view.min.js';
    sdkReady('my', _alipayMpSDK, sdk => {
        if (sdk instanceof Error) {
            callback(sdk);
        }
        else {
            if (window.my) {
                window.__alipayMpReady__ = true;
                callback(window.my);
            }
            else {
                window.__alipayMpReady__ = false;
                callback(sdk);
            }
        }
    });
};
/**
 * 微信小程序webview bridgeReady
 */
const weixinmpReady = (fn) => {
    weixinReady(fn, { needConfig: false });
};
/**
 * 抖音小程序webview bridgeReady
 */
const dympReady = (callback) => {
    const _douyinMpSDK = 'https://lf1-cdn-tos.bytegoofy.com/goofy/developer/jssdk/jssdk-1.1.0.js';
    sdkReady('tt', _douyinMpSDK, sdk => {
        if (sdk instanceof Error) {
            callback(sdk);
        }
        else {
            if (window.tt) {
                window.__douyinMpReady__ = true;
                callback(window.tt);
            }
            else {
                window.__douyinMpReady__ = false;
                callback(sdk);
            }
        }
    });
};
/**
 * 华为快应用webapp bridgeReady
 */
const webappReady = (callback) => {
    if (window.MPXWebviewBridge) {
        callback(window.MPXWebviewBridge);
        return;
    }
    import('@mpxjs/webview-bridge').then(module => {
        window.MPXWebviewBridge = module.default;
        callback(window.MPXWebviewBridge);
    });
};
/**
 * 快应用 ready
 */
const quickBridgeReady = (callback) => {
    // https://git.xiaojukeji.com/webapp/quickbridge 负责人@marcuswang
    const _fastappSDK = 'https://ut-static.udache.com/webapp/web-static/quickapp-bridge/index.js';
    sdkReady('quickappBridge', _fastappSDK, sdk => {
        if (sdk instanceof Error) {
            callback(sdk);
        }
        else {
            if (window.quickappBridge) {
                window.__quickappBridgeReady__ = true;
                callback(window.quickappBridge);
            }
            else {
                window.__quickappBridgeReady__ = false;
                callback(sdk);
            }
        }
    });
};
/**
 * mpReady
 * @description 通用webview做抹平的ready功能
 *   兼容微信/支付宝/抖音
 */
const mpReady = (fn) => {
    if (is('weixin')) {
        weixinmpReady(fn);
        return;
    }
    else if (is('alipay')) {
        alimpReady(fn);
        return;
    }
    else if (is('douyin')) {
        dympReady(fn);
        return;
    }
    else if (xenv() === 'hwfastapp') {
        webappReady(fn);
        return;
    }
};
/**
 * getBridge
 * @description 通用webview获取bridge
 *   兼容微信/支付宝/抖音
 */
function getBridge() {
    if (is('weixin')) {
        return window.wx?.miniProgram;
    }
    else if (is('alipay')) {
        return window.my;
    }
    else if (is('douyin')) {
        return window.tt?.miniProgram;
    }
    else if (xenv() === 'hwfastapp') {
        return window.MPXWebviewBridge;
    }
}
/**
 * mpSetShare
 * @description 通用webview设置分享
 *   兼容微信和支付宝
 */
const mpSetShare = (option) => {
    mpReady(() => {
        if (!getBridge()) {
            window?.Omega?.trackError('webx-js error', 5001, 'setShare执行失败', {
                cdn: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
                xenv: xenv(),
                package: 'web',
                desc: 'setShare因CDN加载失败而执行失败',
            });
        }
        else {
            getBridge().postMessage(option);
        }
    });
};
/**
 * mpDisableShare
 * @description 通用webview禁用分享
 *   兼容微信和支付宝
 */
const mpDisableShare = () => {
    mpReady(() => {
        if (!getBridge()) {
            window?.Omega?.trackError('webx-js error', 5001, 'disableShare执行失败', {
                cdn: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
                xenv: xenv(),
                package: 'web',
                desc: 'disableShare因CDN加载失败而执行失败',
            });
        }
        else {
            getBridge().postMessage({
                data: {
                    type: 'hideShareMenu'
                }
            });
        }
    });
};
/**
 * mpInvokeShare
 * @description 通用webview发起分享
 *   兼容微信
 */
const mpInvokeShare = (option) => {
    if (option.activity_id) {
        mpReady(() => {
            if (!getBridge()) {
                window?.Omega?.trackError('webx-js error', 5001, 'invokeShare执行失败', {
                    cdn: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
                    xenv: xenv(),
                    package: 'web',
                    desc: 'invokeShare因CDN加载失败而执行失败',
                });
            }
            else {
                const transferStationPage = supportNewTransferStationPageXenv.includes(xenv()) ? '/webx/transfer-station/index?type=share' : '/webx-mp-next/transfer-station/index?type=share';
                getBridge().navigateTo({
                    url: build(transferStationPage, option, false)
                });
            }
            // mpBridge.call('share', {
            //   activity_id: option.activity_id || '',
            //   share_title: option.share_title || '',
            //   share_subTitle: option.share_subTitle || '',
            //   share_imgUrl: encodeURIComponent(option.share_imgUrl || ''),
            //   share_sharePath: option.share_sharePath || '',
            //   share_shareImgUrl: option.share_shareImgUrl || '',
            //   poster_img_url: option.poster_img_url || '',
            //   poster_title: option.poster_title || '',
            //   poster_sub_title: option.poster_sub_title || '',
            //   poster_btn_text: option.poster_btn_text || ''
            // })
        });
    }
    else {
        mpInvokeShareMask();
    }
};
const mpInvokeShareV2 = (option) => {
    if (option.activity_id) {
        mpReady(() => {
            if (!getBridge()) {
                window?.Omega?.trackError('webx-js error', 5001, 'invokeShareV2执行失败', {
                    cdn: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
                    xenv: xenv(),
                    package: 'web',
                    desc: 'invokeShareV2因CDN加载失败而执行失败',
                });
            }
            else {
                const transferStationPage = supportNewTransferStationPageXenv.includes(xenv()) ? '/webx/transfer-station/index?type=share' : '/webx-mp-next/transfer-station/index?type=share';
                getBridge().navigateTo({
                    url: build(transferStationPage, option, false)
                });
            }
            // mpBridge.call('share', {
            //   activity_id: option.activity_id || '',
            //   share_title: option.share_title || '',
            //   share_subTitle: option.share_subTitle || '',
            //   share_imgUrl: encodeURIComponent(option.share_imgUrl || ''),
            //   share_sharePath: option.share_sharePath || '',
            //   share_shareImgUrl: option.share_shareImgUrl || '',
            //   poster_img_url: option.poster_img_url || '',
            //   poster_title: option.poster_title || '',
            //   poster_sub_title: option.poster_sub_title || '',
            //   poster_btn_text: option.poster_btn_text || ''
            // })
        });
    }
    else {
        mpSetShare(option);
        mpInvokeShareMask();
    }
};
const getPx = (num) => {
    let radio = document.documentElement.clientWidth / 750;
    return `${num * radio}px`;
};
/**
 * mpInvokeShareMask
 * @description webview环境下 无主动分享机制时 蒙层兜底
 *   兼容支付宝和抖音
 */
const mpInvokeShareMask = () => {
    const shadowImg = '//dpubstatic.udache.com/static/dpubimg/0e3e87f7-215c-48c2-9320-26577ad72bd2.png';
    const $dom = document.createElement('div');
    $dom.style.position = 'fixed';
    $dom.style.width = '100%';
    $dom.style.height = '100%';
    $dom.style.left = '0';
    $dom.style.top = '0';
    $dom.style.backgroundColor = 'rgba(0,0,0,.6)';
    $dom.style.zIndex = '10000';
    $dom.style.backgroundImage = `url(${shadowImg})`;
    $dom.style.backgroundRepeat = 'no-repeat';
    $dom.style.backgroundSize = `${getPx(270)} auto`;
    $dom.style.backgroundPositionY = '0';
    $dom.style.backgroundPositionX = getPx(650 - 270);
    setTimeout(() => {
        // 延时300ms后设置onclick方法，避免移动端点击穿透
        $dom.onclick = () => {
            document.body.removeChild($dom);
        };
    }, 300);
    document.body.appendChild($dom);
};
/**
 * mpSaveImg
 * @description 通用webview保存图片
 *   兼容微信/支付宝/抖音
 */
const mpSaveImg = (option) => {
    mpReady(() => {
        if (!getBridge()) {
            window?.Omega?.trackError('webx-js error', 5001, 'saveImg执行失败', {
                cdn: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
                xenv: xenv(),
                package: 'web',
                desc: 'saveImg因CDN加载失败而执行失败',
            });
        }
        else {
            getBridge().navigateTo({
                url: build('/webx-mp-next/transfer-station/index?type=share', option)
            });
        }
        // mpBridge.call('share', option)
    });
};
/**
 * mpIsLogin
 * @description 通用webview登录
 *   兼容微信/支付宝/抖音
 */
const mpIsLogin = ((option, callback) => {
    return new Promise((resolve, reject) => {
        const queryObj = Object.assign({}, query(), hashQuery());
        const { useQuick } = option || {};
        if (queryObj.token) {
            const userInfo = {
                token: queryObj.token
            };
            /* 获取用户信息成功 存入缓存 */
            storage.userInfo = {
                ...userInfo
            };
            let newRiskParams = Object.assign({}, storage.riskParams, { newTicket: queryObj.token });
            utils.cleanEmptyKey(newRiskParams);
            storage.riskParams = newRiskParams;
            /* 执行传入cb */
            useQuick && callback && callback({ ...userInfo });
            resolve({ ...userInfo });
        }
        else {
            /* 获取用户信息成功 存入缓存 */
            storage.userInfo = {};
            let newRiskParams = Object.assign({}, storage.riskParams, { newTicket: undefined });
            utils.cleanEmptyKey(newRiskParams);
            storage.riskParams = newRiskParams;
            reject({});
        }
    });
});
/**
 * webappIsLogin
 * @description 华为快应用 webapp校验登录
 *   因为webapp postMessage通信有问题，先采用临时方案
 */
const webappIsLogin = () => {
    return new Promise((resolve, reject) => {
        const { token, uid, phone } = Object.assign({}, query(), hashQuery());
        const setWebxStorage = (userInfo) => {
            const token = userInfo.token;
            storage.userInfo = token ? Object.assign({}, storage.userInfo, userInfo) : {};
            const newRiskParams = Object.assign({}, storage.riskParams, { newTicket: token });
            utils.cleanEmptyKey(newRiskParams);
            storage.riskParams = newRiskParams;
        };
        if (token) {
            /* 增加埋点 */
            try {
                window.PerfTrack.customTrack('$_use_quick_isLogin');
                // eslint-disable-next-line no-empty
            }
            catch (_) { }
            /* 获取链接上用户信息 存入缓存 */
            setWebxStorage({ token: token, uid: Number(uid), phone: phone || '' });
            resolve({ token: token, uid: Number(uid), phone: phone || '' });
        }
        else {
            reject({ login: false });
        }
    });
};
/**
 * mpLogin
 * @description 通用webview登录
 *   兼容微信/支付宝/抖音
 */
const mpLogin = ((option) => {
    const _option = option || {};
    _option.back_url = (_option && _option.back_url) ? _option.back_url : location.href.replace(location.hash, '');
    return new Promise((success, fail) => {
        mpReady(() => {
            const jumpUrl = build('/webx-mp-next/transfer-station/index?type=login', _option);
            getBridge().redirectTo({
                url: jumpUrl,
                success: () => {
                    /* 跳转登录页成功 此时并无登录信息返回!! */
                    success({ token: '' });
                },
                fail: () => {
                    /* 跳转登录页失败 此时并无登录信息返回!! */
                    fail({ token: '' });
                }
            });
            // mpBridge.call('login', {
            //   back_url: option.back_url || ''
            // })
        });
    });
});
/**
 * mpSubscribe
 * @description 通用webview订阅
 *   兼容微信和支付宝
 */
const mpSubscribe = (option) => {
    return new Promise((success, fail) => {
        mpReady(() => {
            if (!getBridge()) {
                window?.Omega?.trackError('webx-js error', 5001, 'subscribe执行失败', {
                    cdn: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
                    xenv: xenv(),
                    package: 'web',
                    desc: 'subscribe因CDN加载失败而执行失败',
                });
            }
            else {
                const transferStationPage = supportNewTransferStationPageXenv.includes(xenv()) ? '/webx/transfer-station/index?type=subscribe' : '/webx-mp-next/transfer-station/index?type=subscribe';
                console.log('++++新流程++++');
                getBridge().navigateTo({
                    url: build(transferStationPage, option),
                    success,
                    fail
                });
            }
            // mpBridge.call('subscribe', {
            //   activity_id: option.activity_id || '',
            //   back_url: option.back_url || '',
            //   success,
            //   fail
            // })
        });
    });
};
const getPayStatus = (data) => {
    return new Promise((resolve, reject) => {
        request('POST', 'https://pay.udache.com/gulfstream/pay/v1/didipay/getPayStatus', {
            data: data,
            needSign: true,
            emulateJSON: true,
            giveUpNineChapter: true,
        }).catch(err => {
            reject(err);
        }).then(res => {
            if (res.errno !== 0) {
                reject(res);
            }
            else {
                if (res.data.pay_status === 3) {
                    resolve(res);
                }
                else if (res.data.pay_status === 1) {
                    reject({
                        code: 2,
                        err: res
                    });
                }
                else {
                    reject(res);
                }
            }
        });
    });
};
function stringify(json) {
    if (typeof json === 'string') {
        return json;
    }
    return JSON.stringify(json);
}
/**
 * mpPay
 * @description 通用webview支付
 *   兼容微信/支付宝/抖音
 */
const mpPay = (params) => {
    if (typeof params === 'string') {
        params = {
            options: {
                out_trade_id: params
            }
        };
    }
    let miniPrograms = getPayParams(xenv());
    if (!miniPrograms.mini_url) {
        miniPrograms = getPayParams('wxmp');
    }
    let { pay, options, mini_back, ...args } = params;
    /* 增加埋点 */
    try {
        window.PerfTrack.customTrack('$_use_pay_is_new', {
            is_new: options ? 1 : 0,
        });
        // eslint-disable-next-line no-empty
    }
    catch (_) { }
    const payInfo = utils.merge({}, miniPrograms, {
        options: options || pay || {
            out_trade_id: params.out_trade_id,
            token: params.token,
        },
        ...args
    });
    console.log('payInfo', args, params, payInfo);
    const paramsFormat = payFormatParams(miniProgramFormat, payInfo);
    // 兼容不依赖options的情况
    const payParams = {
        ...params,
        ...paramsFormat,
        options: stringify(paramsFormat.options),
        config: stringify(paramsFormat.config),
    };
    delete payParams.mini_url;
    const back = mini_back || (paramsFormat.mini_url === '/cashier-page/pages/index' && !paramsFormat.successUrl);
    return new Promise((resolve, reject) => {
        back && webUtils.pageShow.once(() => {
            let parseOption = paramsFormat.options;
            try {
                if (typeof parseOption === 'string') {
                    parseOption = JSON.parse(parseOption);
                }
            }
            catch (error) {
                reject(new Error('JSON.parse(parseOption) error'));
            }
            if (!parseOption.out_trade_id) {
                reject(new Error('未获取到 out_trade_id'));
                return;
            }
            getPayStatus({
                out_trade_id: parseOption.out_trade_id,
                token: parseOption.token || ''
            }).then(() => {
                resolve({});
            }).catch(err => {
                reject(err);
            });
        });
        mpReady(() => {
            console.log('miniprogramPay', paramsFormat.mini_url, payParams);
            getBridge().navigateTo({
                url: build(paramsFormat.mini_url, payParams),
                success: () => {
                    if (!back) {
                        resolve({});
                    }
                },
                fail: (err) => {
                    reject(err);
                }
            });
        });
    });
};
/**
 * alimpLocate
 * @description 支付宝小程序webview定位
 *   兼容：因为微信webview需要走wx.config,不适应这套，所以这里只实现支付宝
 */
const alimpLocate = () => {
    return new Promise((resolve, reject) => {
        alimpReady(() => {
            window.my.getLocation({
                success(res) {
                    /* 获取定位信息成功 存入缓存 */
                    storage.locationInfo = {
                        lat: res.latitude,
                        lng: res.longitude
                    };
                    let newRiskParams = Object.assign({}, storage.riskParams, { latitude: res.latitude, longitude: res.longitude });
                    utils.cleanEmptyKey(newRiskParams);
                    storage.riskParams = newRiskParams;
                    resolve({
                        lat: res.latitude,
                        lng: res.longitude
                    });
                },
                fail: (err) => {
                    /* 获取定位信息失败 存入缓存 */
                    storage.locationInfo = {};
                    let newRiskParams = Object.assign({}, storage.riskParams, { latitude: undefined, longitude: undefined });
                    utils.cleanEmptyKey(newRiskParams);
                    storage.riskParams = newRiskParams;
                    reject(err);
                }
            });
        });
    });
};
/**
 * dympLocate
 * @description 抖音小程序webview定位
 */
const dympLocate = () => {
    return new Promise((resolve, reject) => {
        dympReady(() => {
            window.tt.miniProgram.getLocation({
                type: 'gcj02',
                success(res) {
                    /* 获取定位信息成功 存入缓存 */
                    storage.locationInfo = {
                        lat: res.latitude,
                        lng: res.longitude
                    };
                    let newRiskParams = Object.assign({}, storage.riskParams, { latitude: res.latitude, longitude: res.longitude });
                    utils.cleanEmptyKey(newRiskParams);
                    storage.riskParams = newRiskParams;
                    resolve({
                        lat: res.latitude,
                        lng: res.longitude
                    });
                },
                fail: (err) => {
                    /* 获取定位信息失败 存入缓存 */
                    storage.locationInfo = {};
                    let newRiskParams = Object.assign({}, storage.riskParams, { latitude: undefined, longitude: undefined });
                    utils.cleanEmptyKey(newRiskParams);
                    storage.riskParams = newRiskParams;
                    reject(err);
                }
            });
        });
    });
};
/**
 * webappLocate
 * @description 华为快应用webapp定位
 */
const webappLocate = () => {
    return new Promise((resolve, reject) => {
        webappReady(() => {
            window.MPXWebviewBridge.getLocation({
                success(res) {
                    /* 获取定位信息成功 存入缓存 */
                    storage.locationInfo = {
                        lat: res?.latitude,
                        lng: res?.longitude
                    };
                    let newRiskParams = Object.assign({}, storage.riskParams, { latitude: res?.latitude, longitude: res?.longitude });
                    utils.cleanEmptyKey(newRiskParams);
                    storage.riskParams = newRiskParams;
                    resolve({
                        lat: res?.latitude,
                        lng: res?.longitude
                    });
                },
                fail: (err) => {
                    /* 获取定位信息失败 存入缓存 */
                    storage.locationInfo = {};
                    let newRiskParams = Object.assign({}, storage.riskParams, { latitude: undefined, longitude: undefined });
                    utils.cleanEmptyKey(newRiskParams);
                    storage.riskParams = newRiskParams;
                    reject(err);
                }
            });
        });
    });
};
/*
* alimpScanCode
* @description 支付宝小程序webview调起扫码
*/
const alimpScanCode = () => {
    const SCAN_EVENT_TYPE = 'scan';
    return new Promise((resolve, reject) => {
        alimpReady(() => {
            window.my.postMessage({
                data: {
                    type: SCAN_EVENT_TYPE
                }
            });
            window.my.onMessage = (e) => {
                if (e.type === SCAN_EVENT_TYPE) {
                    if (e.result) {
                        resolve(e.result);
                    }
                    else {
                        reject(e.error);
                    }
                }
            };
        });
    });
};
/**
 * wxmpCalendar
 * @description 日历提醒
 */
const wxmpCalendar = (option) => {
    return new Promise((success, fail) => {
        mpReady(() => {
            if (!getBridge()) {
                window?.Omega?.trackError('webx-js error', 5001, 'calendar执行失败', {
                    cdn: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
                    xenv: xenv(),
                    package: 'web',
                    desc: 'calendar因CDN加载失败而执行失败',
                });
            }
            else {
                const transferStationPage = '/webx/transfer-station/index?type=calendar';
                const { extParams = {}, ...rst } = option;
                const { back_url, from } = extParams;
                console.log('++++新流程++++');
                if (back_url) {
                    // 有back_url则会在日历添加时间完成/失败后，重定向回back_url。
                    getBridge().redirectTo({
                        url: build(transferStationPage, { ...rst, from, back_url }),
                        success,
                        fail
                    });
                }
                else {
                    getBridge().navigateTo({
                        url: build(transferStationPage, { ...rst, from }),
                        success,
                        fail
                    });
                }
            }
        });
    });
};
// 将微信/支付宝/抖音的按照功能各自命名分开导出
export { weixinmpReady, weixinLocate as weixinmpLocate, mpIsLogin as weixinmpIsLogin, mpLogin as weixinmpLogin, mpSaveImg as weixinmpSaveImg, mpSubscribe as weixinmpSubscribe, mpSetShare as weixinmpSetShare, mpDisableShare as weixinmpDisableShare, mpInvokeShare as weixinmpInvokeShare, mpInvokeShareV2 as weixinmpInvokeShareV2, mpPay as weixinmpPay, wxCommonJump as weixinmpCommonJump, wxmpCalendar as weixinmpCalendar, };
export { alimpReady, alimpLocate, mpIsLogin as alimpIsLogin, mpLogin as alimpLogin, mpSaveImg as alimpSaveImg, mpSubscribe as alimpSubscribe, mpSetShare as alimpSetShare, mpDisableShare as alimpDisableShare, mpInvokeShareMask as alimpInvokeShare, mpPay as alimpPay, aliCommonJump as alimpCommonJump, mpInvokeShareV2 as alimpInvokeShareV2, mpInvokeShareV2 as sfcalimpInvokeShareV2, alimpScanCode, };
export { dympReady, dympLocate, mpIsLogin as dympIsLogin, mpLogin as dympLogin, mpSaveImg as dympSaveImg, mpSetShare as dympSetShare, mpInvokeShareMask as dympInvokeShare, mpPay as dympPay, dyCommonJump as dympCommonJump };
export { webappReady, webappLocate, mpLogin as webappLogin, mpPay as webappPay, webappCommonJump, webappIsLogin, mpSetShare as webappSetShare, mpInvokeShare as webappInvokeShare, mpInvokeShareV2 as webappInvokeShareV2, quickBridgeReady, };
